import { useContext, useEffect } from "react"
import { Field, FastField, useFormikContext } from "formik"

import _ from "lodash"

import { FormContext } from "../../context/formContext"
import { InputNumber } from "primereact/inputnumber"

import styles from "../../styles/Form.module.css"

import fieldVisibility from "../../helpers/fieldVisibility"

function RegularFieldOutput(props) {
  const { field, isRequired, block, index } = props

  const form = useContext(FormContext)

  let isDisabled = form ? form.blocksSaved[block] : false
  isDisabled = field.read_only ? true : isDisabled

  return (
    <div
      className={styles.field}
      style={{
        order: index,
        display: fieldVisibility(field.field_visibility),
      }}
    >
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <div className={styles.value}>
        <Field name={field.field_name}>
          {({ field: formikField, form, meta }) => {
            return (
              <>
                <InputNumber
                  id={formikField.name}
                  name={formikField.name}
                  value={formikField?.value}
                  showButtons
                  // 'null' is a valid value for min and max.
                  min={field.field_validation.number_min || 0}
                  max={field.field_validation.number_max}
                  pattern="\d+"
                  onChange={({ value }) => {
                    const newValue = value === null ? 0 : value
                    form.setFieldValue(formikField.name, newValue)
                  }}
                  disabled={isDisabled}
                  onBlur={formikField.onBlur}
                />
                {meta.touched && meta.error && (
                  <div className={styles.errFieldMsg}>{meta.error}</div>
                )}
              </>
            )
          }}
        </Field>
      </div>
    </div>
  )
}

function MultiFieldOutput(props) {
  const { field, isRequired, index, mfFieldName, mfIndex } = props
  const fieldSlug = `${mfFieldName}.${mfIndex}.${field.field_name}`
  return (
    <div className={styles.field} style={{ order: index }}>
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <div className={styles.value}>
        <FastField name={fieldSlug}>
          {({ field: fastField, form, meta }) => {
            return (
              <>
                <InputNumber
                  name={fastField.name}
                  id={fastField.name}
                  value={fastField.value}
                  showButtons
                  // 'null' is a valid value for min and max.
                  min={field.field_validation.number_min || 0}
                  max={field.field_validation.number_max}
                  pattern="\d+"
                  onChange={({ value }) => {
                    const newValue = value === null ? 0 : value
                    form.setFieldValue(fastField.name, newValue)
                  }}
                  onBlur={fastField.onBlur}
                />
                {meta.error && (
                  <div className={styles.errFieldMsg}>{meta.error}</div>
                )}
              </>
            )
          }}
        </FastField>
      </div>
    </div>
  )
}

function NestedMultiFieldOutput(props) {
  const {
    field,
    isRequired,
    index,
    mfFieldName,
    mfIndex,
    nestedMfFieldName,
    nestedMfIndex,
  } = props
  const fieldSlug = `${mfFieldName}.${mfIndex}.${nestedMfFieldName}.${nestedMfIndex}.${field.field_name}`
  return (
    <div className={styles.field} style={{ order: index }}>
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <div className={styles.value}>
        <FastField name={fieldSlug}>
          {({ field: fastField, form, meta }) => {
            return (
              <>
                <InputNumber
                  name={fastField.name}
                  id={fastField.name}
                  value={fastField.value}
                  showButtons
                  // 'null' is a valid value for min and max.
                  min={field.field_validation.number_min || 0}
                  max={field.field_validation.number_max}
                  pattern="\d+"
                  onChange={({ value }) => {
                    const newValue = value === null ? 0 : value
                    form.setFieldValue(fastField.name, newValue)
                  }}
                  onBlur={fastField.onBlur}
                />
                {meta.error && (
                  <div className={styles.errFieldMsg}>{meta.error}</div>
                )}
              </>
            )
          }}
        </FastField>
      </div>
    </div>
  )
}

export default function TypeNumber(props) {

  const form = useContext(FormContext)

  useEffect(() => {
    // Register field as loading in FormContext
    form && form.registerLoadingField(props.field)

    // Notify FormContext that field is loaded
    form && form.completeLoadingField(props.field)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const checkIfRequired = () => {
    try {
      if (props.field.field_name === "activity_length_hours") {
        return true
      }
      return JSON.parse(props.field.field_validation.required[0])
    } catch (error) {
      return false
    }
  }
  const isRequired = checkIfRequired()
  switch (props.type) {
    case "regular-field":
      const regFieldProps = {
        field: props.field,
        isRequired,
        block: props.block,
        index: props.index,
      }
      return <RegularFieldOutput {...regFieldProps} />
    case "multi-field":
      const multiFieldProps = {
        field: props.field,
        isRequired,
        index: props.index,
        mfFieldName: props.mfFieldName,
        mfIndex: props.mfIndex,
      }
      return <MultiFieldOutput {...multiFieldProps} />
    case "nested-multi-field":
      const nestedMultiFieldProps = {
        field: props.field,
        isRequired,
        index: props.index,
        mfFieldName: props.mfFieldName,
        mfIndex: props.mfIndex,
        nestedMfFieldName: props.nestedMfFieldName,
        nestedMfIndex: props.nestedMfIndex,
      }
      return <NestedMultiFieldOutput {...nestedMultiFieldProps} />
    default:
      return null
  }
}
